import {useEffect, useLayoutEffect, useRef} from 'react'

const usePrevState = <StateType>(currState: StateType): StateType | null => {
  const prevStateRef = useRef<StateType | null>(null)
  const prevState = prevStateRef.current

  useEffect(() => {
    prevStateRef.current = currState
  })
  return prevState
}

/**
 * This hook replicates the functionality of the `getSnapshotBeforeUpdate` class lifecycle method.
 * See documentation here: https://reactjs.org/docs/react-component.html#getsnapshotbeforeupdate
 *
 * in short, this will generate some snapshot value using the old and new state values and pass it to the `componentDidUpdate` function.
 *
 * In the aforemention function you would preform any updates to the dom/components
 *
 * @param callback A function that generates a snapshot value from the previous state to pass to useComponentDidUpdate
 * @param currState The current value of a particular state value in your component
 *
 * @returns a function similar to componentDidUpdate that will fire before the browser renders the dom
 */
export const useGetSnapshotBeforeUpdate = <StateType, SnapshotType>(
  callback: (prevState: StateType | null) => SnapshotType,
  currState: StateType
) => {
  const prevState = usePrevState(currState)
  const snapshot = useRef<SnapshotType | null>(null)
  const inComponentMount = useRef(true)

  useLayoutEffect(() => {
    !inComponentMount.current && callback(prevState)
    inComponentMount.current = false
  })

  const useComponentDidUpdate = (onUpdate: (prevState: StateType | null, snapshot: SnapshotType | null) => void) => {
    useEffect(() => {
      !inComponentMount.current && onUpdate(prevState, snapshot.current)
    })
  }

  return useComponentDidUpdate
}
